import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import getConfig from "next/config";
import { usePaymentInputs } from "react-payment-inputs";
import { Form, Row, Col } from "react-bootstrap";

import { hexToRGBA } from "../../../../utils/theme";

import ErrorMessage from "../../ErrorMessage";
import ProcessingBlock from "../input-blocks/ProcessingBlock";
import CreditCardInfoBlock from "../input-blocks/CreditCardInfoBlock";
import Button from "../../styled/Button";

const StyledPaymentForm = styled(Form)`
  .page-title {
    padding-top: 8px;
    margin-bottom: 24px;

    font-family: 'InterSemiBold';
    font-size: 26px;
    line-height: calc(1.42857rem * (26 / 16));
    text-align: center;
    color: #424242;
  }

  .inputs-block-title {
    padding: 16px;
    border-bottom: 1px solid ${hexToRGBA('#000000', 0.1)};
    background: ${hexToRGBA('#000000', 0.03)};
    font-family: 'InterMedium';
    font-size: 20px;
    color: #424242;
  }

  .input-title {
    margin-bottom: 4px;
    font-family: 'InterRegular';
    font-size: 14px;
    line-height: calc(1.42857rem * (14 / 16));
    color: ${hexToRGBA('#424242', 0.9)};
    caret-color: ${hexToRGBA('#424242', 0.9)};
  }

  .input-label {
    margin-bottom: 10px;
    font-family: 'InterRegular';
    font-size: 14px;
    line-height: calc(1.42857rem * (14 / 16));
    color: #333333;
  }

  .inputs-block-divider {
    margin-bottom: 16px;
    border-bottom: 1px solid #234e76;
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  .card-providers {
    position: absolute;

    top: calc(18.56px + 5.5px);
    right: calc(15px + 4px);

    img {
      max-height: 24px !important;
      margin-top: 0 !important;
      margin-left: 2px;
      margin-right: 2px;
      /* max-width: calc(25% - 12px / 4); */
      width: auto;
    }

    &.mobile {
      display: none;
    }
  }

  .guarantee-logo {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    margin-bottom: 32px;

    .stamp-container {
      margin-top: 24px;

      img {
        height: 140px;
      }
    }
  }

  .guarantee-disclaimer {
    padding-left: 15%;
    padding-right: 15%;
    font-family: 'InterRegular';
    font-size: 14px;
    line-height: 24px;
    color: #1B3858;
    text-align: center;
    text-shadow: 1px 1px 0px #FFFFFF;
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.laptopL} {
    .card-providers {
      img {
        max-height: 34px;
        margin-top: 9px;
      } 
    }
  }

  @media ${({ theme }) => theme.devices.tablet} {
    padding-bottom: 42px;

    .card-providers {
      top: -7px;
      right: 15px;

      img {
        max-height: 24px !important;
        &:last-child {
          margin-right: 0;
        }
      }

      &.desktop {
        display: none;
      }
    }

    .guarantee-logo {
      padding-left: 5%;
      padding-right: 5%;

      .logo-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        width: 100%;

        img {
          margin-top: 8px;
          margin-bottom: 8px;
        }
      }
    }

    .guarantee-disclaimer {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

function PaymentForm({ error, values = {}, onChange, onSubmit }) {
  const paymentInputs = usePaymentInputs();
  const { meta } = paymentInputs || {};

  const isCardExpired = useMemo(() => {
    if (values.cardYear !== null && values.cardMonth !== null) {
      let expDate = new Date();
      expDate.setFullYear("20" + values.cardYear, values.cardMonth, 1);
      let today = new Date();
      if (expDate < today) {
          return true;
      } else {
          return false;
      }
    }
    return true;
  }, [values]);

  const triggerPAAY = useCallback((e) => {
    let runOnce = true;
    let paayCallback = function (data) {
      const extraValues = { threeDSDetails: data };
      tds.verifyReject = null;
      tds.verifyResolve = null;
      tds.verifyListener = null;
      tds.threeDsTransactionId = null;
      if (runOnce) {
        runOnce = false;
        onChange && onChange({ target: { name: "dLoading", value: false } });
        if (data.status === "Y" || data.status === "A") {
          if (data.protocolVersion.startsWith("2")) {
            extraValues.cavv = data?.authenticationValue;
            extraValues.xid = data?.dsTransId;
            extraValues.acsTransId = data?.acsTransId;
            extraValues.eci = data?.eci;
            extraValues.threeDsStatus = data?.status;
          } else {
            extraValues.cavv = data?.cavv;
            extraValues.xid = data?.xid;
            extraValues.eci = data?.eci;
          }
          //clearInterval(counter);
          onSubmit && onSubmit(e, extraValues);
        } else {
          onSubmit && onSubmit(e, extraValues);
        }
      }
    };
    const successCallbackFunction = paayCallback;
    // if you don't want to accept orders without successful 3DS authentication,
    // make this a separate function that returns decline message to customer
    const failureCallbackFunction = paayCallback;
    if (!document.getElementById("konnektivjs3")) {
      const script3 = document.createElement("script");
      script3.id = "konnektivjs3";
      document.body.appendChild(script3);
      script3.appendChild(
        document.createTextNode(
          'var tds = new ThreeDS("kform", "e03c697d935a4c7d818958ffb1384131", null, { autoSubmit: false, verbose: false, timeout: "10"});'
        )
      );
    }

    tds.verify(successCallbackFunction, failureCallbackFunction, {
      amount: values.currentPrice,
    });

    if (tds.errors.length > 0) {
      const paayError = tds.errors[0];
      console.log("Error: " + paayError);
    }
  }, [values, onChange, onSubmit]);

  const submitCommon = useCallback((e) => {
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();

      const section = document.querySelector(".form-control:invalid");

      if (section != null) {
        section.scrollIntoView({ behavior: "smooth", block: "start"});
      }
    } else {
      window.scrollTo(0, 0);
      const extraValues = {};

      if (values.is3DS && String(process.env.NEXT_PUBLIC_REACT_APP_DS) === "true") {
        onChange && onChange({ target: { name: "dLoading", value: true } });
        const firstCCDigit = values.cardNumber.charAt(0);

        if (firstCCDigit !== "3" && firstCCDigit !== "6" && values.cardNumber !== "0000000000000000") {
          triggerPAAY(e); // Run PAAY authentication
        } else {
          extraValues.threeDSDetails = "3,6 or test card";
          onChange && onChange({ target: { name: "dLoading", value: false } });
          onSubmit && onSubmit(e, extraValues);
        }
      } else {
        extraValues.threeDSDetails = "3ds off";
        onSubmit && onSubmit(e, extraValues);
      }
    }

    onChange && onChange({ target: { name: "validated", value: true } });
  }, [values, onChange, onSubmit, triggerPAAY]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onChange && onChange({ target: { name: "beforeSubmit", value: false } });
    const section1 = document.querySelector(".is-invalid");

    if (section1 != null) {
      section1.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (!values.isRefresh) {
      if (
        (typeof meta.erroredInputs.cardNumber === "undefined" || values.cardNumber === "0000000000000000") && 
        typeof meta.erroredInputs.cvc === "undefined" &&
        values.lastName.length !== 0 &&
        values.firstName.length !== 0 &&
        !isCardExpired
      ) {
        submitCommon(e);
      }
    } else if (
      values.isRefresh &&
      values.lastName.length !== 0 &&
      values.firstName.length !== 0 &&
      !isCardExpired
    ) {
      submitCommon(e);
    }
  }, [onChange, submitCommon, meta, isCardExpired, values]);

  return (
    <StyledPaymentForm
      id="kform"
      noValidate
      validated={values.validated}
      onSubmit={handleSubmit}
    >
      <ProcessingBlock
        values={values}
        onChange={onChange}
      />
      <CreditCardInfoBlock
        isCardExpired={isCardExpired}
        paymentInputs={paymentInputs}
        values={values}
        onChange={onChange}
      />
      {values.is3DS && (
        <>
          <input
            type="hidden"
            name="paayAmount"
            id="paayAmount"
            value={values.currentPrice || ""}
            data-threeds="amount"
          />
          <input
            type="hidden"
            name="x_transaction_id"
            value={values.currentOrder?.crmOrderId}
            data-threeds="id"
          />
        </>
      )}
      {error && (
        <Row>
          <Col xs={12}>
            <ErrorMessage
              message={(
                <span>{error.message}</span>
              )}
            />
          </Col>
          <Col xs={12}>
            <div className="inputs-block-divider" />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={0} md={3} />
        <Col xs={12} md={6}>
          <Button type="submit">
            Submit Application
          </Button>
        </Col>
        <Col xs={0} md={3} />
      </Row>
    </StyledPaymentForm>
  );
}

export default PaymentForm;
